/**
 * @graphql cc
 */
import debounce from 'lodash/debounce';
import type { FC, ReactNode } from 'react';
import React, { useCallback, useEffect, useRef } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useExposeGoogleMeetScreenShare } from '@confluence/embedded-google-meet/entry-points/useExposeGoogleMeetScreenShare.ts';
import { ContentHeader } from '@confluence/content-header/entry-points/ContentHeader.ts';
import type { BaseViewPageProps } from '@confluence/view-page-common/entry-points/BaseViewPageProps.ts';
import { useViewPagePersistentScrollPosition } from '@confluence/view-page-common/entry-points/useViewPagePersistentScrollPosition.ts';
import { useViewPageParams } from '@confluence/view-page-common/entry-points/useViewPageParams.ts';
import { StartViewPageLoad } from '@confluence/view-page-common/entry-points/perf.config.ts';
import { ViewPageCommon } from '@confluence/view-page-common/entry-points/ViewPageCommon.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { waitForGlobal } from '@confluence/wrm/entry-points/waitForGlobal.ts';
import { pagePublishedState } from '@confluence/editor-features/entry-points/pagePublishedState.ts';
import { getCachedPageData } from '@confluence/live-pages-utils/entry-points/getCachedPageData.ts';
import { getLogger } from '@confluence/logger';
import { useSearchSessionId } from '@confluence/search-session/entry-points/SearchSessionState.ts';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import { expVal } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { extractContentFeaturesSkipExpand } from '@confluence/read-time/entry-points/extractContentFeaturesSkipExpand.ts';

import { LegacyBridgeNext } from './LegacyBridgeNext.tsx';
import type {
	ViewPageConfluenceRecordCompanionTipScrollSignalMutation$data,
	ViewPageConfluenceRecordCompanionTipScrollSignalMutation$variables,
} from './__generated__/ViewPageConfluenceRecordCompanionTipScrollSignalMutation.graphql.ts';

export interface ViewPageProps extends BaseViewPageProps {
	classicComments?: React.ComponentType<any> | undefined;
	ClassicEditorContextProviderLoader?: React.ComponentType<any>;
	children?: ReactNode;
}

const COMPANION_TIP_SCROLL_DEPTH_THRESHOLD = 0.8;
const COMPANION_TIP_MIN_BODY_CHARACTER_COUNT = 500;
const logger = getLogger('ViewPage');

type CompanionTipContentBodyDynamic =
	| { representation?: string | null; value?: string | null }
	| null
	| undefined;
type CompanionTipCurrentContentBodyDynamic = {
	contentId: string;
	contentBodyDynamic: CompanionTipContentBodyDynamic;
} | null;

const getCompanionTipScrollDepthY = ({
	scrollY,
	innerHeight,
	scrollHeight,
}: {
	scrollY: number;
	innerHeight: number;
	scrollHeight: number;
}) => {
	if (scrollHeight <= 0) {
		return 0;
	}

	return Math.trunc(((scrollY + innerHeight) / scrollHeight) * 100) / 100;
};

const companionTipBodyHasTextOverCharacterCount = (
	contentBodyDynamic: CompanionTipContentBodyDynamic,
) => {
	if (contentBodyDynamic?.representation !== 'atlas_doc_format' || !contentBodyDynamic.value) {
		return false;
	}

	try {
		const { textLength } = extractContentFeaturesSkipExpand(JSON.parse(contentBodyDynamic.value));

		return textLength > COMPANION_TIP_MIN_BODY_CHARACTER_COUNT;
	} catch {
		return false;
	}
};

const ConfluenceRecordCompanionTipScrollSignalMutation = graphql`
	mutation ViewPageConfluenceRecordCompanionTipScrollSignalMutation($cloudId: ID!, $pageId: ID!) {
		confluence_recordCompanionTipScrollSignal(cloudId: $cloudId, pageId: $pageId) {
			success
			errors {
				message
			}
		}
	}
`;

const ViewPageInternal: FC<ViewPageProps> = ({
	contentId,
	spaceKey,
	classicComments,
	// Pass ClassicEditorContextProviderLoader from parent classic package when ViewPageCommon may need to render LivePageComponent.
	// This is passed down from classic package instead of imported in LivePageComponent because we don't want to import from classic package when in next, it causes circular dependency.
	ClassicEditorContextProviderLoader,
	children,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { versionOverride } = useViewPageParams();
	const [{ searchSessionId, additionalAnalytics }] = useSearchSessionId();
	const { cloudId } = useSessionData();
	const companionTipScrollSignalFiredContentIdRef = useRef<string | null>(null);
	const companionTipUserScrollIntentContentIdRef = useRef<string | null>(null);
	const companionTipContentBodyDynamicRef = useRef<CompanionTipCurrentContentBodyDynamic>(null);

	const [recordCompanionTipScrollSignal] = useMutationInterop<
		ViewPageConfluenceRecordCompanionTipScrollSignalMutation$data,
		ViewPageConfluenceRecordCompanionTipScrollSignalMutation$variables
	>(ConfluenceRecordCompanionTipScrollSignalMutation, { interopApolloAggClient: true });

	const handleContentBodyDynamicChange = useCallback(
		(contentBodyDynamic: CompanionTipContentBodyDynamic, currentContentId?: string | null) => {
			companionTipContentBodyDynamicRef.current = currentContentId
				? {
						contentId: currentContentId,
						contentBodyDynamic,
					}
				: null;
		},
		[],
	);

	useViewPagePersistentScrollPosition(contentId);

	// A/A test for the Confluence FY26 H2 holdout. Contact ##twc-fy26-holdout-working-group for questions.
	useEffect(() => {
		expVal('confluence_aa_test', 'isEnabled', false);
	}, []);

	let contentHeader: React.ReactNode = null;
	if (contentId && spaceKey) {
		contentHeader = (
			<ContentHeader
				spaceKey={spaceKey}
				contentId={contentId}
				// @ts-ignore - Type 'number | null' is not assignable to type 'number | undefined'
				// This error was introduced after upgrading to TypeScript 5
				versionOverride={versionOverride}
			/>
		);
	}

	const spaceId = useSpaceId(spaceKey);

	useEffect(() => {
		// event is triggered by legacy JS when a new jira issue is created via JIM dialog
		waitForGlobal({ listener: 'ViewPage', globalProperty: 'AJS.bind' }, () => {
			window.AJS.bind('createGASv3Event', (_, data) => {
				createAnalyticsEvent(data).fire();
			});
		});

		if (pagePublishedState.pageJustPublished) {
			pagePublishedState.pageJustPublished = false;
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'viewed',
					actionSubject: 'pagePublished',
					source: 'viewPage',
					objectId: contentId,
					containerId: spaceId,
					containerType: 'space',
				},
			}).fire();
		}

		return () => {
			waitForGlobal({ listener: 'ViewPage', globalProperty: 'AJS.unbind' }, () => {
				window.AJS.bind('createGASv3Event');
			});
		};
	}, [createAnalyticsEvent, contentId, spaceId]);

	// Adds the `page scrolled (client)` instrumentation event to be used in the new metric definition for *engaged* users.
	useEffect(() => {
		const handleScroll = () => {
			const { scrollX, scrollY, innerWidth, innerHeight } = window;
			const { scrollWidth, scrollHeight } = document.documentElement;

			// The user has reached the bottom of the page if `scrollY + innerHeight` equals `scrollHeight`.
			const attributes = {
				scrollX,
				scrollY,
				innerWidth,
				innerHeight,
				scrollWidth,
				scrollHeight,

				// The depths are rounded to two decimals; that's enough precision for scrolling.
				depthX: Math.trunc(((scrollX + innerWidth) / scrollWidth) * 100) / 100,
				depthY: Math.trunc(((scrollY + innerHeight) / scrollHeight) * 100) / 100,
				searchSessionId,
				...additionalAnalytics,
			};
			const companionTipScrollDepthY = getCompanionTipScrollDepthY({
				scrollY,
				innerHeight,
				scrollHeight,
			});

			// Gate the signal on scroll depth, identifiers, once-per-content, user intent, and experiment enrollment.
			const hasCompanionTipScrollSignalPrerequisites =
				companionTipScrollDepthY >= COMPANION_TIP_SCROLL_DEPTH_THRESHOLD &&
				Boolean(cloudId) &&
				Boolean(contentId) &&
				companionTipScrollSignalFiredContentIdRef.current !== contentId &&
				companionTipUserScrollIntentContentIdRef.current === contentId &&
				expVal('cc_companion_tip_emails_api_exp', 'isEnabled', false);

			// Only trust body.dynamic when it belongs to the currently viewed content.
			const companionTipCurrentContentBodyDynamic =
				companionTipContentBodyDynamicRef.current?.contentId === contentId
					? companionTipContentBodyDynamicRef.current.contentBodyDynamic
					: undefined;

			// Before the tracker callback runs, body.dynamic is unknown, so let the backend decide.
			const hasNoCompanionTipBodyDynamicTrackerState =
				companionTipContentBodyDynamicRef.current === null;
			// During SPA transitions, stale body data belongs to a previous contentId.
			const hasStaleCompanionTipBodyDynamicForDifferentContent =
				companionTipContentBodyDynamicRef.current !== null &&
				companionTipContentBodyDynamicRef.current.contentId !== contentId;
			// Fast live-page rendering can confirm the current content before body.dynamic is available.
			const hasUnavailableCompanionTipBodyDynamicForCurrentContent =
				companionTipContentBodyDynamicRef.current !== null &&
				companionTipContentBodyDynamicRef.current.contentId === contentId &&
				companionTipCurrentContentBodyDynamic === undefined;

			const shouldUseBackendCompanionTipBodyLengthFilter =
				hasNoCompanionTipBodyDynamicTrackerState ||
				hasStaleCompanionTipBodyDynamicForDifferentContent ||
				hasUnavailableCompanionTipBodyDynamicForCurrentContent;

			// Fire only when local ADF length passes or the backend is deliberately left to decide.
			const companionTipBodyHasRequiredLength =
				hasCompanionTipScrollSignalPrerequisites &&
				(shouldUseBackendCompanionTipBodyLengthFilter ||
					companionTipBodyHasTextOverCharacterCount(companionTipCurrentContentBodyDynamic));

			// Mark before firing so retries/rejections cannot duplicate the signal for this content.
			if (
				hasCompanionTipScrollSignalPrerequisites &&
				companionTipBodyHasRequiredLength &&
				cloudId &&
				contentId
			) {
				companionTipScrollSignalFiredContentIdRef.current = contentId;
				void recordCompanionTipScrollSignal({
					variables: {
						cloudId,
						pageId: contentId,
					},
				}).catch(() => {
					logger.warn`Failed to record companion tip scroll signal`;
				});
			}

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'scrolled',
					actionSubject: 'page',
					source: 'viewPage',
					objectId: contentId,
					containerId: spaceId,
					containerType: 'space',
					attributes,
				},
			})?.fire(); // used optional chaining so that scroll event can be tested - mocking fire causes infinite loop
		};

		// Once Safari supports the `scrollend` event we can remove the `debounce` logic on the `scroll` event and use `scrollend` intead.
		// https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollend_event
		const debounceScroll = debounce(handleScroll, 250 /*ms*/);
		const scrollContainer = document.getElementById('AkMainContent') ?? document;
		const setCompanionTipUserScrollIntent = () => {
			companionTipUserScrollIntentContentIdRef.current = contentId;
		};

		// Treat keyboard navigation keys as explicit scroll intent for non-pointer users.
		const handleCompanionTipKeyboardScrollIntent = ({ key }: KeyboardEvent) => {
			if (
				key === 'ArrowDown' ||
				key === 'ArrowUp' ||
				key === 'PageDown' ||
				key === 'PageUp' ||
				key === 'Home' ||
				key === 'End' ||
				key === ' '
			) {
				setCompanionTipUserScrollIntent();
			}
		};

		if (expVal('cc_companion_tip_emails_api_exp', 'isEnabled', false)) {
			scrollContainer.addEventListener('wheel', setCompanionTipUserScrollIntent, {
				passive: true,
			});
			scrollContainer.addEventListener('touchmove', setCompanionTipUserScrollIntent, {
				passive: true,
			});
			document.addEventListener('keydown', handleCompanionTipKeyboardScrollIntent);
		}
		scrollContainer.addEventListener('scroll', debounceScroll);
		return () => {
			scrollContainer.removeEventListener('scroll', debounceScroll);
			scrollContainer.removeEventListener('wheel', setCompanionTipUserScrollIntent);
			scrollContainer.removeEventListener('touchmove', setCompanionTipUserScrollIntent);
			document.removeEventListener('keydown', handleCompanionTipKeyboardScrollIntent);
		};
	}, [
		createAnalyticsEvent,
		contentId,
		spaceId,
		searchSessionId,
		additionalAnalytics,
		cloudId,
		recordCompanionTipScrollSignal,
	]);

	const cachedPageData = getCachedPageData(contentId);

	useExposeGoogleMeetScreenShare();

	return (
		<>
			<StartViewPageLoad contentId={contentId} />
			<ViewPageCommon
				spaceKey={spaceKey}
				contentId={contentId}
				contentHeader={contentHeader}
				classicComments={classicComments}
				hasPageComments
				hasLabels
				hasReactions
				hasInlineComments
				hasEOPRecs
				ClassicEditorContextProviderLoader={ClassicEditorContextProviderLoader}
				cachedPageData={cachedPageData}
				onCurrentContentBodyDynamicChange={handleContentBodyDynamicChange}
			>
				{children}
				<LegacyBridgeNext />
			</ViewPageCommon>
		</>
	);
};

export const ViewPage: typeof ViewPageInternal = (props) => {
	return (
		<SuspenseWithDataFetcher dataFetcherName="content-main">
			<UFOSegment name="view-page">
				<ViewPageInternal {...props} />
			</UFOSegment>
		</SuspenseWithDataFetcher>
	);
};
